/**
 * A utility class for managing disposable behaviors in stores.
 * This allows for consistent cleanup and reinitialization of behaviors
 * like autorun, reactions, or other subscriptions.
 */
export class DisposableBehavior {
  private disposer?: () => void;

  /**
   * Initializes the behavior with the provided setup function.
   * Automatically cleans up any existing behavior before setting up the new one.
   * @param setup A function that returns a cleanup function
   */
  initialize(setup: () => () => void): void {
    this.dispose();
    this.disposer = setup();
  }

  /**
   * Cleans up the current behavior if one exists.
   */
  dispose(): void {
    if (this.disposer) {
      this.disposer();
      this.disposer = undefined;
    }
  }
}

/**
 * Creates a new disposable behavior instance.
 * @returns A new DisposableBehavior instance
 */
export const createDisposableBehavior = () => new DisposableBehavior();
